import React from "react"
import { Box, ButtonBase, Typography } from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"

import { dsColorNew } from "@palette"


// One section of a long form as a card: icon, title, an optional hint ("optional") and, on the right of the
// title line, the switch that belongs to the whole section. The body only renders when it has something to show.
export default function AppFormSection({ icon, title, hint, action, children, ...props }) {
	const hasBody = React.Children.toArray(children).some(Boolean)

	return <Box component="section" {...props} sx={{
		backgroundColor: dsColorNew.white,
		border: `1px solid ${dsColorNew.grayBorder}`,
		borderRadius: '14px',
		...props?.sx,
	}}>
		<Box sx={{
			display: 'flex', alignItems: 'center', gap: 1.25, px: 2.25, py: 1.75,
			...(hasBody && { borderBottom: `1px solid ${dsColorNew.grayBorder}` }),
		}}>
			{!!icon && <Box sx={{
				width: 28, height: 28, borderRadius: '8px', flexShrink: 0,
				display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
				backgroundColor: dsColorNew.pillActiveBg, color: dsColorNew.appBlue,
			}}>
				<IconSeeker icon={icon} sx={{ fontSize: 13 }}/>
			</Box>}
			<Typography component="h3" sx={{ fontSize: 14, fontWeight: 600, color: dsColorNew.textTitle }}>{title}</Typography>
			{!!hint && <Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>{hint}</Typography>}
			{!!action && <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>{action}</Box>}
		</Box>
		{hasBody && <Box sx={{
			p: 2.25,
			// labels that hide the field border behind them (date pickers) take the card's white
			'& .MuiInputLabel-root.MuiInputLabel-shrink': { backgroundColor: dsColorNew.white },
		}}>{children}</Box>}
	</Box>
}

// The section's fields on a 12-column grid; give each child `gridColumn: 'span N'`
export function AppFormGrid({ children, sx = {} }) {
	return <Box sx={{
		display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 2, alignItems: 'start',
		// the framework's date pickers size their box to 98% of a stretched cell; grid cells here are not stretched
		'& .rs-picker-toggle[role="combobox"]': { minHeight: 37 },
		...sx,
	}}>
		{children}
	</Box>
}

// Optional extras of a section behind a quiet link: the same show / hide the old "More Features" switch did
export function AppMoreOptions({ open, onToggle, children, label = 'More options', ...props }) {
	return <Box {...props} sx={{ gridColumn: '1 / -1', ...props?.sx }}>
		<ButtonBase
			aria-expanded={!!open}
			onClick={onToggle}
			sx={{ gap: 0.75, px: 0.5, py: 0.25, borderRadius: '6px', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', color: dsColorNew.appBlue, '&:hover': { backgroundColor: dsColorNew.hoverBgLight } }}
		>
			<IconSeeker icon={open ? 'minus' : 'plus'} sx={{ fontSize: 11 }}/>
			{open ? 'Fewer options' : label}
		</ButtonBase>
		{!!open && <Box sx={{ mt: 1.75, pt: 1.75, borderTop: `1px dashed ${dsColorNew.grayBorder}` }}>
			<Typography sx={{ fontSize: 11, fontWeight: 600, color: dsColorNew.textSubtle, textTransform: 'uppercase', letterSpacing: 0.6, mb: 1.5 }}>{label}</Typography>
			{children}
		</Box>}
	</Box>
}
